import { Editor } from '@monaco-editor/react';
import { useCallback, useEffect, useState } from 'react';
import type { GoogleDriveFile } from '../../api/google';
import { googleDriveFilesUpdate } from '../../api/google';
import { vscodeTheme } from '../../styles/theme';
import { useToast } from '../shared/Toast';

interface GoogleDriveEditorProps {
  file: GoogleDriveFile;
  initialContent: string;
  initialRevision?: string;
  onRevisionChange?: (revision?: string) => void;
  onFileUpdated?: () => void;
}

type ApiError = Error & { status?: number };

export function GoogleDriveEditor({
  file,
  initialContent,
  initialRevision,
  onRevisionChange,
  onFileUpdated,
}: GoogleDriveEditorProps) {
  const { showToast } = useToast();
  const [content, setContent] = useState(initialContent);
  const [revision, setRevision] = useState(initialRevision);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setContent(initialContent);
    setRevision(initialRevision);
    setError(null);
    setStatusMessage('');
  }, [file.id, initialContent, initialRevision]);

  const saveFile = useCallback(async () => {
    try {
      setSaving(true);
      setError(null);
      const response = await googleDriveFilesUpdate({
        file_id: file.id,
        content,
        if_match: revision,
        revision,
      });
      const nextRevision = response.headRevisionId ?? revision;
      setRevision(nextRevision);
      if (onRevisionChange) {
        onRevisionChange(nextRevision);
      }
      setStatusMessage('Saved!');
      setTimeout(() => setStatusMessage(''), 2000);
      if (onFileUpdated) {
        onFileUpdated();
      }
    } catch (err) {
      const errorWithStatus = err as ApiError;
      if (errorWithStatus.status === 409) {
        showToast('Conflict: file updated by someone else', 'error');
        setError('Conflict: file updated by someone else');
        return;
      }
      const message =
        err instanceof Error ? err.message : 'Failed to save Google Drive file';
      setError(message);
      showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  }, [content, file.id, onFileUpdated, onRevisionChange, revision, showToast]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        void saveFile();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [saveFile]);

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          padding: '8px 16px',
          borderBottom: `1px solid ${vscodeTheme.colors.border}`,
          backgroundColor: vscodeTheme.colors.headerBackground,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          position: 'relative',
        }}
      >
        <span
          style={{
            color: vscodeTheme.colors.textPrimary,
            fontSize: vscodeTheme.typography.fontSize,
            fontFamily: vscodeTheme.typography.fontFamily,
            flex: 1,
          }}
        >
          {file.name} ({file.id})
        </span>
        {statusMessage && (
          <span
            style={{
              color: vscodeTheme.colors.textPrimary,
              fontSize: vscodeTheme.typography.fontSize,
              fontFamily: vscodeTheme.typography.fontFamily,
            }}
          >
            {statusMessage}
          </span>
        )}
        <button
          onClick={() => void saveFile()}
          disabled={saving}
          style={{
            padding: '6px 10px',
            backgroundColor: 'transparent',
            color: saving
              ? vscodeTheme.colors.textSecondary
              : vscodeTheme.colors.textPrimary,
            border: 'none',
            borderRadius: '2px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            transition: 'opacity 0.1s',
          }}
          onMouseEnter={(event) => {
            if (!saving) {
              event.currentTarget.style.opacity = '0.7';
            }
          }}
          onMouseLeave={(event) => {
            if (!saving) {
              event.currentTarget.style.opacity = '1';
            }
          }}
          title="Save"
        >
          {saving ? '⏳' : '💾'}
        </button>
      </div>
      {error && (
        <div style={{ padding: '8px 16px', color: vscodeTheme.colors.error }}>
          {error}
        </div>
      )}
      <Editor
        height="100%"
        defaultLanguage="json"
        value={content}
        onChange={(value) => setContent(value || '')}
        theme="vs-dark"
        options={{
          minimap: { enabled: false },
          fontSize: 14,
          readOnly: false,
          bracketPairColorization: { enabled: true },
        }}
      />
    </div>
  );
}
